import React, { useMemo, useState } from 'react'
import Tooltip from '@mui/material/Tooltip'
import { Status as StatusComponent } from 'Components/admin/aarPartnerData'
import {
  Body,
  BodyTitle,
  BodyHeader,
  BodyContent,
} from 'Components/shared/body'
import { TableFooter } from 'Components/shared/table'
import { TablePagination } from 'Containers/shared/tablePagination'

import { Loading } from 'Utils/loading'
import { usePagination } from 'Hooks/usePagination'
import { useGetOrangeReportProcessLogsQuery } from 'Services/admin/orangeReports'
import { useGetInstitutionsInfoQuery } from 'Services/global/institution'

const sortColumnData = (sortOrder) => [
  { columnName: 'College ID', columnWidth: 100 },
  { columnName: 'College Name', columnWidth: 180 },
  { columnName: 'Status', columnWidth: 80 },
  { columnName: 'Created At', columnWidth: 150 },
  { columnName: 'Updated At', columnWidth: 150, sortOrder: sortOrder, sortBy: 'updated_at' },
  { columnName: 'Error Message', columnWidth: 300 },
]

const ErrorMessageCell = ({ message }) => {
  if (!message) return ''

  const truncatedMessage = message.length > 50 ? `${message.substring(0, 50)}...` : message

  return message.length > 50 ? (
    <Tooltip title={message} placement="top">
      <span style={{ cursor: 'pointer' }}>{truncatedMessage}</span>
    </Tooltip>
  ) : (
    <span>{message}</span>
  )
}

const DateTimeCell = ({ date }) => {
  const shortFormat = new Date(date).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

  const fullFormat = new Date(date).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  })

  return (
    <Tooltip title={fullFormat} placement="top">
      <span style={{ cursor: 'pointer' }}>{shortFormat}</span>
    </Tooltip>
  )
}

const CollegeNameCell = ({ name }) => {
  if (!name) return ''

  const truncatedName = name.length > 25 ? `${name.substring(0, 25)}...` : name

  return name.length > 25 ? (
    <Tooltip title={name} placement="top">
      <span style={{ cursor: 'pointer' }}>{truncatedName}</span>
    </Tooltip>
  ) : (
    <span>{name}</span>
  )
}

export const Status = () => {
  const PAGE_SIZE = 20

  const { currentPage, handlePageChange, setCurrentPage } = usePagination()
  const [sortOrder, setSortOrder] = useState('desc')

  const { data: processLogData, isLoading, isFetching, refetch } = useGetOrangeReportProcessLogsQuery({
    page: currentPage,
    size: PAGE_SIZE,
    order: sortOrder,
  })

  const { data: institutionsData, isLoading: isLoadingInstitutions } = useGetInstitutionsInfoQuery()

  const institutionMap = useMemo(() => {
    if (!institutionsData) return {}
    return institutionsData.reduce((acc, institution) => {
      acc[institution.id] = institution.name
      return acc
    }, {})
  }, [institutionsData])

  const flattenProcessLogs = useMemo(() => {
    let rows = []
    if (processLogData) {
      processLogData?.data?.records?.forEach((log: any) => {
        const collegeName = institutionMap[log.institution_id] || 'Unknown College'
        rows.push([
          '',
          log.institution_id,
          <CollegeNameCell key={`college-${log.id}`} name={collegeName} />,
          log.status,
          <DateTimeCell key={`created-${log.id}`} date={log.created_at} />,
          <DateTimeCell key={`updated-${log.id}`} date={log.updated_at} />,
          <ErrorMessageCell key={`error-${log.id}`} message={log.error_message} />,
        ])
      })
    }
    return rows
  }, [processLogData, institutionMap])

  return (
    <Body>
      {isLoading || isFetching || isLoadingInstitutions ? <Loading /> : null}
      <BodyHeader>
        <BodyTitle>AAR Partner Data - Upload Status</BodyTitle>
      </BodyHeader>
      <BodyContent>
        {processLogData?.data?.total_records > 0 ? (
          <>
            <StatusComponent
              columnData={sortColumnData(sortOrder)}
              setSortOrder={setSortOrder}
              rows={flattenProcessLogs}
            />
            <TableFooter>
              {processLogData?.data?.total_pages > 1 && (
                <TablePagination
                  rowsPerPage={PAGE_SIZE}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                  totalRecords={processLogData?.data?.total_records}
                  totalPages={processLogData?.data?.total_pages}
                />
              )}
            </TableFooter>
          </>
        ) : !(isLoading || isFetching || isLoadingInstitutions) && 'No Record Found'}
      </BodyContent>
    </Body>
  )
}